<template>
  <div>
    <div v-if="$apollo.loading || cancellationState === 'checking'">
      <spinner />
    </div>
    <div v-else>
      <span v-if="status === 'SUCCESSFUL'">{{
        $t('paymentDone', 'The payment has already been made')
      }}</span>
      <div v-else>
        <div class="failed-payment-container">
          <div class="failed-payment-title-container">
            <div v-if="slug" class="failed-back-to-event" @click="goBack">
              <img
                class="pagination-arrow"
                src="/assets/data-table-icons/arrow-left.svg"
                alt="Go left"
              >
              Natrag na događaj
            </div>
            <div class="failed-title">{{ cancellationState === 'confirmed' ? $t('paymentCanceledTitle') : $t('paymentCancellationUnconfirmedTitle') }}</div>
            <div class="failed-subtitle">{{ cancellationState === 'confirmed' ? $t('paymentCanceledMessage') : $t('paymentCancellationUnconfirmedMessage') }}</div>
            <button v-if="cancellationState !== 'confirmed'" type="button" @click="checkAgain">
              {{ $t('paymentCheckStatus') }}
            </button>
          </div>

          <div v-if="cancellationState === 'confirmed'" class="failed-tips-container">
            <div class="failed-tips-title">Što možete učiniti dalje?</div>
            <div class="failed-tips-text">
              <p>
                💳   Provjerite stanje na računu ili podatke o kartici.
              </p>
              <p>🔁   Pokušajte ponovno izvršiti plaćanje.</p>
              <p>✅   Ne zaboravite autorizirati plaćanje putem vašeg internet bankarstva.</p>
              <p>📧   Ako se ovaj problem i dalje ponavlja, čak i nakon provjere s Vašom bankom, slobodno nam se obratite putem kontakt obrasca – rado ćemo pomoći.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from '../shared/spinner'
import { GET_EVENT_SETTINGS } from '../../graphql/event.graphql'
import { GET_ORDER } from '../../graphql/order.graphql'
import { updateOrder } from '../../http'

export default {
  name: 'canceled-payment',
  components: {
    Spinner,
  },
  props: {
    orderUUID: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      status: '',
      slug: undefined,
      cancellationState: 'checking',
      cancellationRequestedUuid: null,
    }
  },
  apollo: {
    getOrder: {
      query: GET_ORDER,
      fetchPolicy: 'network-only',
      variables() {
        return {
          uuid: this.orderUUID,
        }
      },
      async result({ loading, data }) {
        if (loading) return
        if (!data || !data.getOrder) {
          this.cancellationState = 'unconfirmed'

          return
        }

        const { eventId, status } = data.getOrder
        this.status = status
        this.cancellationState = 'checking'

        let eventData
        try {
          const result = await this.$apollo.query({
            query: GET_EVENT_SETTINGS,
            variables: { id: eventId },
          })
          eventData = result.data
        } catch (_) {
          this.cancellationState = 'unconfirmed'

          return
        }
        if (eventData && eventData.getEvent) {
          this.slug = eventData.getEvent.slug
        }

        if (status === 'SUCCESSFUL' || status === 'FAILED') {
          this.cancellationState = 'confirmed'

          return
        }
        // A fresh status read must not silently dispatch another cancellation.
        if (this.cancellationRequestedUuid === this.orderUUID) {
          this.cancellationState = 'unconfirmed'

          return
        }
        this.cancellationRequestedUuid = this.orderUUID
        try {
          const order = await updateOrder(this.orderUUID, { status: 'FAILED' })
          if (!order || !['FAILED', 'SUCCESSFUL'].includes(order.status)) {
            this.cancellationState = 'unconfirmed'

            return
          }
          this.status = order.status
          this.cancellationState = 'confirmed'
        } catch (_) {
          // A pending provider payment or an ambiguous network response is not a
          // confirmed cancellation. Keep the held order and offer a status read.
          this.cancellationState = 'unconfirmed'
        }
      },
      error() {
        this.cancellationState = 'unconfirmed'
      },
    },
  },
  methods: {
    async checkAgain() {
      // Even when the initial order/event read failed before cancellation,
      // this explicitly labelled status action must remain read-only.
      this.cancellationRequestedUuid = this.orderUUID
      this.cancellationState = 'checking'
      try {
        await this.$apollo.queries.getOrder.refetch()
      } catch (_) {
        this.cancellationState = 'unconfirmed'
      }
    },
    goBack() {
      if (!this.slug) return
      this.goTo(this.routes.shared.register, {
        eventSlug: this.slug,
        orderNumber: this.orderUUID,
        status: this.cancellationState === 'confirmed' ? this.status : 'PURCHASING',
      })
    },
  },
}
</script>

<style scoped>
.failed-payment-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  text-align: center; /* Optional, to center text */
}

.pagination-arrow {
  width: 6.5px;
  height: 11px;
  margin-right: 6px;
}

.failed-payment-title-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Align text and items to the left */
  text-align: left; /* Ensure text aligns left */
  width: 100%;
  max-width: 493px; /* Match tips container if you want same width */
}

.failed-back-to-event {
  font-family: Raleway;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  letter-spacing: 1.5%;
  text-transform: uppercase;
  margin-bottom: 24px;
  cursor: pointer;
}

.failed-title {
  font-family: Raleway;
  font-weight: 800;
  font-size: 28px;
  line-height: 120%;
  text-align: start;
}

.failed-subtitle {
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 18px;
  line-height: 150%;
  text-align: start;
  margin-top: 8px;
}

.failed-tips-container {
  background-color: #F5F5F5;
  border-radius: 16px;
  padding: 24px;
  margin: 24px auto 0;
  width: 100%;
  max-width: 493px;
  box-sizing: border-box;
  text-align: start;
}

.failed-tips-title {
  font-family: Raleway;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  margin-bottom: 12px;
}

.failed-tips-text p {
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  margin-bottom: 0px;
  word-wrap: break-word;
}

/* Optional: Fine-tune for smaller devices */
@media (max-width: 480px) {
  .failed-title {
    font-size: 24px;
  }

  .failed-subtitle,
  .failed-tips-text p {
    font-size: 16px;
  }

  .failed-tips-title {
    font-size: 18px;
  }

  .failed-tips-container {
    padding: 16px;
  }
}
</style>
